import { useState, useEffect, useRef, useCallback } from 'react';
import API from '../api/axiosConfig';

const STORAGE_KEY = 'qkiere_popup_shown';

/* ── Tiny inline icons (no extra deps) ── */
function XIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
function ChevLeft() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}
function ChevRight() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
function ExternalIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

function navigateTo(url) {
  if (!url) return;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    // Internal route — navigate in-app
    window.location.href = url.startsWith('/') ? url : `/${url}`;
  }
}

/* ── Mosaic grid mode ── */
function MosaicGrid({ slides, closing, onClose }) {
  const count = slides.length;
  const cols  = count === 1 ? 1 : count === 2 ? 2 : count === 3 ? 3 : count <= 4 ? 2 : 3;
  const maxW  = cols === 1 ? 440 : cols === 2 ? 540 : 620;

  return (
    <>
      <style>{`
        @keyframes qk-fi  { from { opacity:0 } to { opacity:1 } }
        @keyframes qk-si  { from { opacity:0; transform:scale(.88) translateY(24px) }
                            to   { opacity:1; transform:scale(1)   translateY(0)    } }
        @keyframes qk-fo  { from { opacity:1 } to { opacity:0 } }
        @keyframes qk-so  { from { opacity:1; transform:scale(1)   }
                            to   { opacity:0; transform:scale(.94) } }
      `}</style>
      <div onClick={onClose} style={{
        position:'fixed', inset:0, zIndex:9999,
        background:'rgba(0,0,0,0.88)', backdropFilter:'blur(10px)',
        animation: closing ? 'qk-fo .28s ease forwards' : 'qk-fi .3s ease forwards',
        display:'flex', alignItems:'center', justifyContent:'center', padding:16,
      }}>
        <div onClick={e => e.stopPropagation()} style={{
          position:'relative', width:'100%', maxWidth:maxW,
          background:'#0d0d0d', borderRadius:24, overflow:'hidden',
          boxShadow:'0 40px 100px rgba(0,0,0,.8), 0 0 0 1px rgba(255,255,255,.06)',
          animation: closing ? 'qk-so .28s ease forwards' : 'qk-si .38s cubic-bezier(.34,1.2,.64,1) forwards',
        }}>
          <button onClick={onClose} aria-label="Cerrar" style={{
            position:'absolute', top:12, right:12, zIndex:10,
            width:34, height:34, borderRadius:'50%',
            background:'rgba(0,0,0,.55)', border:'1px solid rgba(255,255,255,.15)',
            display:'flex', alignItems:'center', justifyContent:'center',
            color:'rgba(255,255,255,.8)', cursor:'pointer', backdropFilter:'blur(4px)',
            transition:'background .15s',
          }}
            onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,.15)'}
            onMouseLeave={e => e.currentTarget.style.background='rgba(0,0,0,.55)'}>
            <XIcon size={15} />
          </button>

          <div style={{ display:'grid', gridTemplateColumns:`repeat(${cols},1fr)`, gap:3, padding:3 }}>
            {slides.map((slide, i) => (
              <div key={slide.id || i}
                onClick={() => slide.buttonUrl && navigateTo(slide.buttonUrl)}
                style={{
                  position:'relative', cursor: slide.buttonUrl ? 'pointer' : 'default',
                  aspectRatio: count === 1 ? 'auto' : '1 / 1',
                  overflow:'hidden', borderRadius:6, background:'#111',
                }}>
                <img src={slide.image} alt={slide.title || `Imagen ${i+1}`}
                  style={{
                    width:'100%',
                    height: count === 1 ? 'auto' : '100%',
                    maxHeight: count === 1 ? '65vh' : undefined,
                    objectFit: count === 1 ? 'contain' : 'cover',
                    display:'block',
                  }} />
                {slide.title && (
                  <div style={{
                    position:'absolute', bottom:0, left:0, right:0,
                    padding: count === 1 ? '28px 14px 10px' : '16px 8px 7px',
                    background:'linear-gradient(to top,rgba(0,0,0,.85) 0%,transparent 100%)',
                    fontSize: count === 1 ? 15 : 11, fontWeight:800,
                    color:'white', lineHeight:1.2,
                    whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
                  }}>{slide.title}</div>
                )}
                {slide.buttonUrl && count > 1 && (
                  <div style={{
                    position:'absolute', top:6, right:6, width:20, height:20, borderRadius:'50%',
                    background:'rgba(254,196,0,.9)',
                    display:'flex', alignItems:'center', justifyContent:'center',
                  }}>
                    <ExternalIcon />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div style={{ textAlign:'center', padding:'10px 0 14px', background:'#0d0d0d' }}>
            <button onClick={onClose} style={{
              background:'none', border:'none',
              color:'rgba(255,255,255,.25)', fontSize:11, cursor:'pointer', transition:'color .15s',
            }}
              onMouseEnter={e => e.currentTarget.style.color='rgba(255,255,255,.5)'}
              onMouseLeave={e => e.currentTarget.style.color='rgba(255,255,255,.25)'}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/* ── Main popup ── */
export default function LandingPopup() {
  const [config,    setConfig]    = useState(null);
  const [visible,   setVisible]   = useState(false);
  const [current,   setCurrent]   = useState(0);
  const [closing,   setClosing]   = useState(false);
  const [imgLoaded, setImgLoaded] = useState({});
  const autoRef  = useRef(null);
  const touchRef = useRef(null);

  /* Fetch config */
  useEffect(() => {
    API.get('/settings/popup-banner')
      .then(res => {
        const cfg = res.data?.data?.config ?? res.data?.config;
        if (!cfg?.active)          return;
        if (!cfg?.slides?.length)  return;
        if (cfg.showOnce && sessionStorage.getItem(STORAGE_KEY)) return;
        setConfig(cfg);
        const delay = Math.max(0, Number(cfg.delaySeconds ?? 1)) * 1000;
        setTimeout(() => setVisible(true), delay);
      })
      .catch(() => {/* silent — popup is best-effort */});
  }, []);

  /* Auto-advance slides */
  useEffect(() => {
    if (!visible || !config || config.slides.length <= 1) return;
    autoRef.current = setInterval(() => {
      setCurrent(c => (c + 1) % config.slides.length);
    }, 4500);
    return () => clearInterval(autoRef.current);
  }, [visible, config]);

  /* ESC to close */
  useEffect(() => {
    if (!visible) return;
    const h = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [visible]);

  const close = useCallback(() => {
    setClosing(true);
    if (config?.showOnce) sessionStorage.setItem(STORAGE_KEY, '1');
    setTimeout(() => { setVisible(false); setClosing(false); }, 280);
  }, [config]);

  const prev = () => { clearInterval(autoRef.current); setCurrent(c => (c - 1 + config.slides.length) % config.slides.length); };
  const next = () => { clearInterval(autoRef.current); setCurrent(c => (c + 1) % config.slides.length); };
  const goTo = (i) => { clearInterval(autoRef.current); setCurrent(i); };

  /* Touch swipe */
  const onTouchStart = (e) => { touchRef.current = e.touches[0].clientX; };
  const onTouchEnd   = (e) => {
    if (touchRef.current == null) return;
    const diff = touchRef.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) diff > 0 ? next() : prev();
    touchRef.current = null;
  };

  if (!visible || !config) return null;

  const slides  = config.slides;
  const hasMany = slides.length > 1;

  if (config.layout === 'mosaic') {
    return <MosaicGrid slides={slides} closing={closing} onClose={close} />;
  }

  const slide = slides[current];

  return (
    <>
      <style>{`
        @keyframes qk-fi  { from { opacity:0 } to { opacity:1 } }
        @keyframes qk-si  { from { opacity:0; transform:scale(.88) translateY(24px) }
                            to   { opacity:1; transform:scale(1)   translateY(0)    } }
        @keyframes qk-fo  { from { opacity:1 } to { opacity:0 } }
        @keyframes qk-so  { from { opacity:1; transform:scale(1)   }
                            to   { opacity:0; transform:scale(.94) } }
        @keyframes qk-prog { from { width:0% } to { width:100% } }
        @keyframes qk-spin { to   { transform:rotate(360deg) } }
      `}</style>

      {/* Backdrop */}
      <div onClick={close} style={{
        position:'fixed', inset:0, zIndex:9999,
        background:'rgba(0,0,0,0.88)', backdropFilter:'blur(10px)',
        WebkitBackdropFilter:'blur(10px)',
        animation: closing ? 'qk-fo .28s ease forwards' : 'qk-fi .3s ease forwards',
        display:'flex', alignItems:'center', justifyContent:'center', padding:16,
      }}>
        {/* Card */}
        <div onClick={e => e.stopPropagation()}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          style={{
            position:'relative', width:'100%', maxWidth:440,
            background:'#0d0d0d', borderRadius:24, overflow:'hidden',
            boxShadow:'0 40px 100px rgba(0,0,0,.8), 0 0 0 1px rgba(255,255,255,.06)',
            animation: closing
              ? 'qk-so .28s ease forwards'
              : 'qk-si .38s cubic-bezier(.34,1.2,.64,1) forwards',
          }}>

          {/* Close button */}
          <button onClick={close} aria-label="Cerrar" style={{
            position:'absolute', top:12, right:12, zIndex:10,
            width:34, height:34, borderRadius:'50%',
            background:'rgba(0,0,0,.55)', border:'1px solid rgba(255,255,255,.15)',
            display:'flex', alignItems:'center', justifyContent:'center',
            color:'rgba(255,255,255,.8)', cursor:'pointer',
            backdropFilter:'blur(4px)', transition:'background .15s',
          }}
            onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,.15)'}
            onMouseLeave={e => e.currentTarget.style.background='rgba(0,0,0,.55)'}>
            <XIcon size={15} />
          </button>

          {/* Progress bars */}
          {hasMany && (
            <div style={{ position:'absolute', top:0, left:0, right:0, height:3, zIndex:10,
              display:'flex', gap:2 }}>
              {slides.map((_, i) => (
                <div key={i} style={{ flex:1, background:'rgba(255,255,255,.15)', overflow:'hidden' }}>
                  {i === current && (
                    <div key={`${current}-p`} style={{
                      height:'100%', background:'#FEC400',
                      animation:'qk-prog 4.5s linear forwards',
                    }} />
                  )}
                  {i < current && (
                    <div style={{ height:'100%', background:'rgba(254,196,0,.7)', width:'100%' }} />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Image */}
          <div style={{
            position:'relative', width:'100%',
            cursor: slide.buttonUrl ? 'pointer' : 'default',
            background:'#111', display:'flex', alignItems:'center', justifyContent:'center', minHeight:200,
          }}
            onClick={() => slide.buttonUrl && navigateTo(slide.buttonUrl)}>
            <img key={slide.image} src={slide.image} alt={slide.title || `Slide ${current+1}`}
              onLoad={() => setImgLoaded(p => ({ ...p, [slide.image]: true }))}
              style={{
                width:'100%', maxHeight:'65vh', objectFit:'contain', display:'block',
                transition:'opacity .3s', opacity: imgLoaded[slide.image] ? 1 : 0,
              }} />

            {/* Spinner while loading */}
            {!imgLoaded[slide.image] && (
              <div style={{ position:'absolute', inset:0,
                display:'flex', alignItems:'center', justifyContent:'center' }}>
                <div style={{
                  width:32, height:32, border:'3px solid rgba(254,196,0,.3)',
                  borderTopColor:'#FEC400', borderRadius:'50%',
                  animation:'qk-spin .8s linear infinite',
                }} />
              </div>
            )}

            {/* Bottom gradient */}
            {(slide.title || slide.description || slide.buttonText) && (
              <div style={{
                position:'absolute', bottom:0, left:0, right:0, height:'45%',
                background:'linear-gradient(to top,rgba(0,0,0,.9) 0%,transparent 100%)',
                pointerEvents:'none',
              }} />
            )}

            {/* Arrows */}
            {hasMany && (
              <>
                {[{ dir:'left', fn: e => { e.stopPropagation(); prev(); } },
                  { dir:'right', fn: e => { e.stopPropagation(); next(); } }]
                  .map(({ dir, fn }) => (
                    <button key={dir} onClick={fn} style={{
                      position:'absolute', top:'50%', transform:'translateY(-50%)',
                      [dir]: 10,
                      width:36, height:36, borderRadius:'50%',
                      background:'rgba(0,0,0,.5)', border:'1px solid rgba(255,255,255,.15)',
                      display:'flex', alignItems:'center', justifyContent:'center',
                      color:'white', cursor:'pointer', backdropFilter:'blur(4px)', transition:'background .15s',
                    }}
                      onMouseEnter={e => e.currentTarget.style.background='rgba(0,0,0,.8)'}
                      onMouseLeave={e => e.currentTarget.style.background='rgba(0,0,0,.5)'}>
                      {dir === 'left' ? <ChevLeft /> : <ChevRight />}
                    </button>
                  ))}
              </>
            )}
          </div>

          {/* Text content */}
          {(slide.title || slide.description || slide.buttonText) && (
            <div style={{ padding:'18px 20px 22px', background:'#0d0d0d' }}>
              {slide.title && (
                <h3 style={{ color:'white', fontSize:18, fontWeight:900,
                  letterSpacing:'-.3px', margin:0, lineHeight:1.2 }}>
                  {slide.title}
                </h3>
              )}
              {slide.description && (
                <p style={{ color:'rgba(255,255,255,.6)', fontSize:13, lineHeight:1.5,
                  margin: slide.title ? '6px 0 0' : 0 }}>
                  {slide.description}
                </p>
              )}
              {slide.buttonText && slide.buttonUrl && (
                <button onClick={() => navigateTo(slide.buttonUrl)} style={{
                  marginTop:14, display:'inline-flex', alignItems:'center', gap:8,
                  background:'linear-gradient(135deg,#FEC400,#f59e0b)',
                  color:'#1c1917', border:'none', borderRadius:12,
                  padding:'10px 20px', fontSize:14, fontWeight:900, cursor:'pointer',
                  boxShadow:'0 4px 16px rgba(254,196,0,.35)', transition:'transform .12s, box-shadow .12s',
                  letterSpacing:'.01em',
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform='scale(1.03)'; e.currentTarget.style.boxShadow='0 6px 22px rgba(254,196,0,.5)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform='scale(1)'; e.currentTarget.style.boxShadow='0 4px 16px rgba(254,196,0,.35)'; }}>
                  {slide.buttonText}
                  <ExternalIcon />
                </button>
              )}
            </div>
          )}

          {/* Dot indicators */}
          {hasMany && (
            <div style={{ display:'flex', justifyContent:'center', gap:6,
              padding:'10px 20px 14px', background:'#0d0d0d' }}>
              {slides.map((_, i) => (
                <button key={i} onClick={() => goTo(i)} aria-label={`Slide ${i+1}`} style={{
                  width: i === current ? 20 : 6, height:6, borderRadius:3,
                  background: i === current ? '#FEC400' : 'rgba(255,255,255,.2)',
                  border:'none', cursor:'pointer', transition:'width .25s, background .25s', padding:0,
                }} />
              ))}
            </div>
          )}

          {/* Dismiss */}
          <div style={{ textAlign:'center', paddingBottom:14, background:'#0d0d0d' }}>
            <button onClick={close} style={{
              background:'none', border:'none',
              color:'rgba(255,255,255,.25)', fontSize:11, cursor:'pointer', transition:'color .15s',
            }}
              onMouseEnter={e => e.currentTarget.style.color='rgba(255,255,255,.5)'}
              onMouseLeave={e => e.currentTarget.style.color='rgba(255,255,255,.25)'}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
